import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Button, withStyles, getContrastColor } from 'vopay';
import { useLayout } from '../../providers/Layout.provider';
import { usePaylink } from '../../providers/Paylink.provider';
import useStorage from '../../hooks/useStorage';
import usePostGenerateOtp from '../../hooks/useApi/usePostGenerateOtp';
import useValidateOtp from '../../hooks/useApi/useValidateOtp';
import styles from './OTP.module.scss';

type OTP = {
  Code1: string;
  Code2: string;
  Code3: string;
  Code4: string;
  Code5: string;
  Code6: string;
};

const buildOtpFormRows = () => [
  {
    id: 'Code1',
  },
  {
    id: 'Code2',
  },
  {
    id: 'Code3',
  },
  {
    id: 'Code4',
  },
  {
    id: 'Code5',
  },
  {
    id: 'Code6',
  },
];

const defaultValues: OTP = {
  Code1: '',
  Code2: '',
  Code3: '',
  Code4: '',
  Code5: '',
  Code6: '',
};

const OTPForm: FunctionComponent = () => {
  const history = useHistory();
  const classes = withStyles(styles);
  const storage = useStorage();
  const { t } = useTranslation('translations', { keyPrefix: 'OTP' });
  const { Token } = storage.paylinkToken.getPaylinkToken();
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const { validateOtp, pending } = useValidateOtp();
  const { generateOtp } = usePostGenerateOtp();
  const [isNewOtpSent, setIsNewOtpSent] = useState(false);
  const [isResendButtonEnabled, setIsResendButtonEnabled] = useState(false);
  const [isContinueButtonEnabled, setIsContinueButtonEnabled] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState('');
  const [resendOtpTimeLeft, setResendOtpTimeLeft] = useState({
    min: 1,
    sec: 0,
  });

  useEffect(() => {
    if (!isResendButtonEnabled) {
      const timer = setInterval(() => {
        setResendOtpTimeLeft((prevTime) => {
          let newMin = prevTime.min;
          let newSec = prevTime.sec - 1;

          if (newSec < 0) {
            newSec = 59;
            newMin -= 1;
          }

          if (newMin < 0) {
            clearInterval(timer);
            return { min: 0, sec: 0 };
          }

          return { min: newMin, sec: newSec };
        });
      }, 1000);

      return () => clearInterval(timer);
    }

    return () => {};
  }, [resendOtpTimeLeft, isResendButtonEnabled]);

  useEffect(() => {
    if (resendOtpTimeLeft.min === 0 && resendOtpTimeLeft.sec === 0) {
      setIsResendButtonEnabled(true);
      setResendOtpTimeLeft({
        min: 1,
        sec: 0,
      });
    }
  }, [resendOtpTimeLeft]);

  useEffect(() => {
    if (paylink.ReceiverPhoneNumber !== '') {
      generateOtp({
        SingleUseToken: Token,
        PhoneNumber: paylink.ReceiverPhoneNumber || '',
      });
      setIsResendButtonEnabled(false);
    }
  }, [paylink]);

  useEffect(() => {
    if (isNewOtpSent) {
      const timer = setTimeout(() => {
        setIsNewOtpSent(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isNewOtpSent]);

  const handleSendOtp = async () => {
    setOtpErrorMessage('');
    setIsNewOtpSent(false);
    const res = await generateOtp({
      SingleUseToken: Token,
      PhoneNumber: paylink.ReceiverPhoneNumber || '',
    });
    if (!res.Success) {
      console.error(res.ErrorMessage);
      return;
    }
    setIsNewOtpSent(true);
    setIsResendButtonEnabled(false);
  };

  return (
    <Formik initialValues={defaultValues} onSubmit={() => {}}>
      {({ handleChange, handleBlur, values, setFieldValue }) => {
        const isAutoFocusRef = useRef(false);

        const autoInputFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
          const { maxLength, value, name } = e.target;

          const fieldIndex = name.substr(4, 1);
          const fieldIntIndex = parseInt(fieldIndex, 10);

          if (value.length >= maxLength) {
            if (fieldIntIndex < 6) {
              const nextfield = document.querySelector(`input[name=Code${fieldIntIndex + 1}]`);
              if (nextfield !== null) {
                isAutoFocusRef.current = true;
                (nextfield as HTMLElement)?.focus();
              }
            }
          }
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
          const pastedText = e.clipboardData.getData('text/plain');
          if (pastedText) {
            setFieldValue('Code1', pastedText.charAt(0));
            setFieldValue('Code2', pastedText.charAt(1));
            setFieldValue('Code3', pastedText.charAt(2));
            setFieldValue('Code4', pastedText.charAt(3));
            setFieldValue('Code5', pastedText.charAt(4));
            setFieldValue('Code6', pastedText.charAt(5));
            isAutoFocusRef.current = true;
            const lastField = document.querySelector('input[name=Code6]');
            (lastField as HTMLElement)?.focus();
          }
        };

        const handleBackSpace = (e: React.KeyboardEvent<HTMLInputElement>, fieldIndex: number) => {
          if (e.key === 'Backspace' && fieldIndex > 1) {
            const previousField = document.querySelector(`input[name=Code${fieldIndex - 1}]`);
            if (previousField !== null) {
              (previousField as HTMLElement)?.focus();
              setFieldValue(`Code${fieldIndex}`, '');
            }
          }
        };

        useEffect(() => {
          const isFormComplete = Object.values(values).every((val) => val !== '');
          setIsContinueButtonEnabled(isFormComplete);
        }, [values]);

        return (
          <form className={classes('form')}>
            <Box flx="col" gapSize="24px">
              <Box gapSize="12px" height="auto" jc="center">
                {buildOtpFormRows().map(({ id }, index) => (
                  <div className={classes('textField')} key={id}>
                    <TextField
                      key={id}
                      name={id}
                      value={values[id as keyof OTP]}
                      variant="lined"
                      preset="text"
                      borderColor={otpErrorMessage ? '#d22d2d' : '#6b6b6b'}
                      padding="0px"
                      maxLength={1}
                      disabled={pending}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        autoInputFocus(e);
                      }}
                      onFocus={() => {
                        setOtpErrorMessage('');
                        if (!isAutoFocusRef.current) {
                          setFieldValue(id, '');
                        }
                        isAutoFocusRef.current = false;
                      }}
                      onPaste={(e) => id === 'Code1' && handlePaste(e)}
                      onKeyDown={(e) => handleBackSpace(e, index + 1)}
                    />
                  </div>
                ))}
              </Box>

              <Box flx="col" gapSize="24px" height="auto">
                <Box gapSize="12px" width="100%" height="auto">
                  <Button
                    id="resendOtp-button"
                    label={
                      <>
                        {t('resendButton')}
                        <span className={classes('timeLimit')}>
                          {!isResendButtonEnabled &&
                            `(${String(resendOtpTimeLeft.min).padStart(1, '0')}:${String(
                              resendOtpTimeLeft.sec,
                            ).padStart(2, '0')})`}
                        </span>
                      </>
                    }
                    width="100%"
                    height="48px"
                    fontUnit="px"
                    textSize={16}
                    fontWeight={700}
                    background={isResendButtonEnabled ? '#fbfbfb' : '#dedede'}
                    textColor={isResendButtonEnabled ? 'black' : 'grey'}
                    isDisabled={!isResendButtonEnabled || pending}
                    round
                    noPadding
                    onClick={() => {
                      handleSendOtp();
                    }}
                  />
                  <Button
                    id="continue-button"
                    label={t('continueButton')}
                    width="100%"
                    height="48px"
                    fontUnit="px"
                    textSize={16}
                    fontWeight={700}
                    background={isContinueButtonEnabled ? color : '#dedede'}
                    textColor={isContinueButtonEnabled ? getContrastColor(color) : 'grey'}
                    isDisabled={!isContinueButtonEnabled || pending}
                    round
                    onClick={async () => {
                      const res = await validateOtp({
                        SingleUseToken: Token,
                        OTP: `${values.Code1}${values.Code2}${values.Code3}${values.Code4}${values.Code5}${values.Code6}`,
                      });

                      if (res.Success) {
                        history.push('/connect/payment-method');
                      } else {
                        const errorMessage = res?.ErrorMessage.includes('No OTP found')
                          ? t('noOtpErrorMessage')
                          : res?.ErrorMessage.includes('Too many failed OTP attempts')
                          ? t('failedOtpErrorMessage')
                          : res?.ErrorMessage.includes('Your OTP has expired')
                          ? t('expiredOtpErrorMessage')
                          : res?.ErrorMessage.includes('The OTP you entered is invalid')
                          ? t('invalidOtpErrorMessage')
                          : '';
                        setOtpErrorMessage(errorMessage);
                      }
                    }}
                  />
                </Box>

                <div className={classes('messageContainer')}>
                  {isNewOtpSent && <div className={classes('message', 'position')}>{t('newOtpMessage')}</div>}
                  {otpErrorMessage !== '' && <div className={classes('message', 'error')}>{otpErrorMessage}</div>}
                </div>
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

export default OTPForm;
